import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { useEffect, useRef, useState } from 'react';
import { liveServiceAvailable } from './liveService.js';
import { onMicYield } from './micHold.js';

/**
 * "Deixar na mesa": com o app aberto e a tela acesa, em qualquer tela, o aparelho
 * fica ouvindo só a chamada ("Ô, <nome>") no próprio celular; ao ouvir, a conversa
 * ao vivo abre pelo caminho de sempre. Só no app Android, só com o app à vista,
 * só com a chave ligada pela pessoa. Minimizado ou com a tela apagada, para; quando
 * outro recurso pede o microfone (ditado, mensagem de voz), solta e espera.
 * A tela só fica acesa sozinha na Início (o celular na mesa).
 */

export type WakeState = 'off' | 'downloading' | 'ready' | 'listening' | 'error';

type WakePlugin = {
  startWake(options: { names: string[] }): Promise<void>;
  stopWake(): Promise<void>;
  wakeStatus(): Promise<{ state: WakeState; modelReady: boolean }>;
  keepScreenOn(options: { on: boolean }): Promise<void>;
  addListener(
    event: 'wake',
    handler: (data: { name: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'wakeState',
    handler: (data: { state: WakeState; progress: number; detail?: string }) => void,
  ): Promise<PluginListenerHandle>;
};

const plugin = registerPlugin<WakePlugin>('SecretarioVoice');

/** Chave do perfil: `preferences.deskMode === true`. */
export const DESK_PREF = 'deskMode';
/** Chamadas por hora, no máximo: a TV ligada não vira conversa atrás de conversa. */
export const WAKES_PER_HOUR = 12;
/** A chamada abriu a conversa há pouco: a conversa espera a primeira frase antes de gastar minutos. */
const WAKE_WINDOW_MS = 4000;
let wokeAt = 0;

export function deskAvailable(): boolean {
  return liveServiceAvailable();
}

/** A conversa que está abrindo veio da chamada pelo nome? (vale uma vez) */
export function consumeWake(): boolean {
  const recent = Date.now() - wokeAt < WAKE_WINDOW_MS;
  wokeAt = 0;
  return recent;
}

/** Som curto de "oi": duas notas, sem modelo, para a pessoa saber que foi ouvida. */
export function chime(): void {
  try {
    const context = new AudioContext();
    const now = context.currentTime;
    for (const [at, freq] of [
      [0, 660],
      [0.12, 880],
    ] as const) {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.18, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.18);
      osc.connect(gain).connect(context.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.2);
    }
    setTimeout(() => void context.close(), 600);
  } catch {
    /* sem som: a vibração e a tela já avisam */
  }
}

const HOUR_KEY = 'secretario.mesa.chamadas';

/** Quantas chamadas aconteceram na última hora (neste aparelho). */
function wakesLastHour(): number[] {
  try {
    const raw = window.sessionStorage.getItem(HOUR_KEY);
    const list: number[] = raw ? (JSON.parse(raw) as number[]) : [];
    const cutoff = Date.now() - 60 * 60 * 1000;
    return list.filter((at) => at > cutoff);
  } catch {
    return [];
  }
}

function rememberWake(): boolean {
  const list = wakesLastHour();
  if (list.length >= WAKES_PER_HOUR) return false;
  list.push(Date.now());
  try {
    window.sessionStorage.setItem(HOUR_KEY, JSON.stringify(list));
  } catch {
    /* sem memória: segue valendo nesta abertura */
  }
  return true;
}

export type DeskStatus = {
  state: WakeState;
  progress: number;
  /** As frases que o aparelho está esperando (ex.: "ô sol"). */
  phrases: string[];
  /** No erro, o motivo que o aparelho mandou ("sem_microfone" quando falta a permissão). */
  reason?: string;
};

/**
 * Liga e desliga o ouvir do nome conforme a chave, a tela e o lugar. `onWake`
 * recebe o nome chamado; quem chama abre a conversa.
 */
export function useDeskMode({
  enabled,
  names,
  onWake,
  keepAwake = true,
}: {
  enabled: boolean;
  names: string[];
  onWake: (name: string) => void;
  /** Mantém a tela acesa enquanto ouve (só na Início). */
  keepAwake?: boolean;
}): DeskStatus {
  const [status, setStatus] = useState<DeskStatus>({ state: 'off', progress: 0, phrases: [] });
  const wakeRef = useRef(onWake);
  wakeRef.current = onWake;
  const key = names.join('|');

  useEffect(() => {
    if (!enabled || !deskAvailable()) {
      setStatus({ state: 'off', progress: 0, phrases: [] });
      return;
    }
    let alive = true;
    let stopWake = () => {};
    let stopState = () => {};
    const start = () => {
      if (!alive || document.visibilityState !== 'visible') return;
      void plugin.startWake({ names: key.split('|').filter(Boolean) }).catch(() => {
        setStatus((current) => ({ ...current, state: 'error' }));
      });
    };
    void plugin
      .addListener('wake', (data) => {
        if (!alive) return;
        if (!rememberWake()) return;
        wokeAt = Date.now();
        wakeRef.current(data.name);
      })
      .then((handle) => {
        stopWake = () => void handle.remove();
      });
    void plugin
      .addListener('wakeState', (data) => {
        if (!alive) return;
        // As frases vêm com o "pronto"; o "ouvindo" que vem logo depois não as traz de novo.
        setStatus((current) => ({
          state: data.state,
          progress: data.progress,
          ...(data.state === 'error' && data.detail ? { reason: data.detail } : {}),
          phrases:
            data.state === 'error' || data.state === 'off'
              ? []
              : data.detail
                ? data.detail.split('|')
                : current.phrases,
        }));
      })
      .then((handle) => {
        stopState = () => void handle.remove();
      });
    // Volta a ouvir quando o app volta à tela (o lado nativo para sozinho ao sair).
    const onVisible = () => {
      if (document.visibilityState === 'visible') start();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Outro recurso vai usar o microfone: solta já, antes de ele começar.
    onMicYield(() => plugin.stopWake());
    start();
    return () => {
      alive = false;
      onMicYield(null);
      document.removeEventListener('visibilitychange', onVisible);
      stopWake();
      stopState();
      void plugin.stopWake().catch(() => {});
      setStatus({ state: 'off', progress: 0, phrases: [] });
    };
  }, [enabled, key]);

  // Tela acesa só onde o celular fica na mesa (a Início); nas outras telas, o normal.
  const awake = enabled && keepAwake && deskAvailable();
  useEffect(() => {
    if (!awake) return;
    void plugin.keepScreenOn({ on: true }).catch(() => {});
    return () => void plugin.keepScreenOn({ on: false }).catch(() => {});
  }, [awake]);

  return status;
}
